// src/pages/admin/ManageFeedback.js - FIXED VERSION
import React, { useState, useEffect } from 'react';
import {
  FaStar,
  FaEye,
  FaTrash,
  FaExclamationTriangle,
  FaComments,
  FaFilter,
  FaSearch,
  FaClock,
  FaCheckCircle,
  FaSync,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaImage,
  FaThumbtack,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ManageFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    fetchAllFeedbacks();
    const interval = setInterval(fetchAllFeedbacks, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllFeedbacks = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');

      if (!token) {
        setError('Please login as admin to access this page');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/feedback/admin/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch feedbacks');
      }

      const data = await response.json();
      
      // DEBUG: Log the response
      console.log('📦 API Response:', {
        success: data.success,
        count: data.count,
        feedbacksCount: data.feedbacks?.length,
        firstFeedback: data.feedbacks?.[0],
        idField: data.feedbacks?.[0] ? Object.keys(data.feedbacks[0]).filter(k => k.includes('id') || k.includes('Id')) : 'none'
      });

      if (Array.isArray(data)) {
        setFeedbacks(data);
      } else if (data.feedbacks && Array.isArray(data.feedbacks)) {
        // FIX: Ensure we have proper IDs
        const processedFeedbacks = data.feedbacks.map(f => ({
          ...f,
          // Ensure we use _id (MongoDB/Supabase uses _id)
          id: f._id || f.id // Use _id if available, fallback to id
        }));
        setFeedbacks(processedFeedbacks);
      } else {
        console.error('Unexpected data format:', data);
        setFeedbacks([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError(error.message || 'Failed to load feedbacks');
      setFeedbacks([]);
      setLoading(false);
    }
  };

  // FIXED: Get correct ID from feedback
  const getFeedbackId = (feedback) => {
    // Try _id first (MongoDB), then id
    const id = feedback._id || feedback.id;
    
    if (!id) {
      console.error('❌ No ID found in feedback:', feedback);
      return null;
    }
    
    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      console.warn('⚠️ ID is not UUID format:', id);
    }
    
    return id;
  };

  const handleDelete = async (feedback) => {
    const feedbackId = getFeedbackId(feedback);
    
    if (!feedbackId) {
      alert('❌ Cannot delete: Invalid feedback ID');
      return;
    }

    if (
      !window.confirm(
        '⚠️ Are you sure you want to DELETE this feedback? This action cannot be undone and the feedback will be removed from the homepage immediately.'
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/feedback/${feedbackId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        alert('✅ Feedback deleted successfully!');
        fetchAllFeedbacks();
        setSelectedFeedback(null);
      } else {
        const data = await response.json();
        alert(`❌ ${data.error || 'Failed to delete feedback'}`);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('❌ Failed to delete feedback');
    }
  };

  // FIXED: Toggle Pin Handler
  const handleTogglePin = async (feedback, currentPinStatus) => {
    const feedbackId = getFeedbackId(feedback);
    
    if (!feedbackId) {
      alert('❌ Cannot pin: Invalid feedback ID');
      return;
    }

    console.log('🔍 Toggling pin for feedback ID:', feedbackId);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/api/feedback/${feedbackId}/pin`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();
      
      if (response.ok) {
        // Update local feedback state
        const updatedFeedbacks = feedbacks.map(fb => {
          const fbId = getFeedbackId(fb);
          if (fbId === feedbackId) {
            return { 
              ...fb, 
              is_pinned: responseData.isPinned || !currentPinStatus 
            };
          }
          return fb;
        });
        
        setFeedbacks(updatedFeedbacks);

        // Update selected feedback if it's open
        if (selectedFeedback) {
          const selectedId = getFeedbackId(selectedFeedback);
          if (selectedId === feedbackId) {
            setSelectedFeedback({ 
              ...selectedFeedback, 
              is_pinned: responseData.isPinned || !currentPinStatus 
            });
          }
        }

        alert(
          responseData.isPinned
            ? '📌 Feedback pinned to homepage!'
            : '📌 Feedback unpinned from homepage!'
        );
      } else {
        alert(`❌ ${responseData.error || 'Failed to toggle pin'}`);
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
      alert('❌ Failed to toggle pin');
    }
  };

  const handleDeleteReply = async (feedback, replyId) => {
    const feedbackId = getFeedbackId(feedback);
    
    if (!feedbackId) {
      alert('❌ Cannot delete reply: Invalid feedback ID');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${API_URL}/api/feedback/${feedbackId}/reply/${replyId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        alert('✅ Reply deleted successfully!');
        await fetchAllFeedbacks();

        const updated = feedbacks.find(f => getFeedbackId(f) === feedbackId);
        if (updated) {
          const updatedFeedback = {
            ...updated,
            replies: (updated.replies || []).filter(r => r._id !== replyId && r.id !== replyId),
          };
          setSelectedFeedback(updatedFeedback);
        }
      } else {
        const data = await response.json();
        alert(`❌ ${data.error || 'Failed to delete reply'}`);
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      alert('❌ Failed to delete reply');
    }
  };

  const filteredFeedbacks = feedbacks
    .filter(feedback => {
      if (filterRating !== 'all' && feedback.rating !== parseInt(filterRating, 10))
        return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          feedback.name.toLowerCase().includes(query) ||
          feedback.email.toLowerCase().includes(query) ||
          feedback.message.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'rating-desc':
          return b.rating - a.rating;
        case 'rating-asc':
          return a.rating - b.rating;
        default:
          return 0;
      }
    });

  const stats = {
    total: feedbacks.length,
    today: feedbacks.filter(f => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return new Date(f.createdAt) >= today;
    }).length,
    averageRating:
      feedbacks.length > 0
        ? (feedbacks.reduce((sum, f) => sum + f.rating, 0) / feedbacks.length).toFixed(1)
        : 0,
    withReplies: feedbacks.filter(f => f.replies && f.replies.length > 0).length,
    pinned: feedbacks.filter(f => f.is_pinned).length,
  };

  if (error && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 rounded-2xl p-8 max-w-md">
            <FaExclamationTriangle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-800 dark:text-red-300 mb-2">
              Access Denied
            </h2>
            <p className="text-red-700 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={() => (window.location.href = '/admin/login')}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition"
            >
              Go to Admin Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && feedbacks.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading feedbacks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-3">
                <FaComments className="text-blue-600" />
                Manage Client Feedback
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                All feedback is published instantly. Review and moderate content.
              </p>
            </div>
            <button
              onClick={fetchAllFeedbacks}
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center gap-2 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Debug Panel - Remove in production */}
        <div className="mb-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg text-sm">
          <div className="flex justify-between items-center">
            <span className="font-semibold">Debug Info:</span>
            <button 
              onClick={() => console.log('Feedbacks:', feedbacks)}
              className="text-xs bg-blue-500 text-white px-2 py-1 rounded"
            >
              Log Data
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div>Total: {stats.total}</div>
            <div>With _id: {feedbacks.filter(f => f._id).length}</div>
            <div>With id: {feedbacks.filter(f => f.id).length}</div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaComments className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Total Feedback</div>
            <div className="text-4xl font-bold">{stats.total}</div>
            <p className="text-sm opacity-75 mt-1">All time</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaCheckCircle className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Published Today</div>
            <div className="text-4xl font-bold">{stats.today}</div>
            <p className="text-sm opacity-75 mt-1">Live on homepage</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaStar className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Average Rating</div>
            <div className="text-4xl font-bold">{stats.averageRating}</div>
            <p className="text-sm opacity-75 mt-1">Out of 5 stars</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaComments className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">With Replies</div>
            <div className="text-4xl font-bold">{stats.withReplies}</div>
            <p className="text-sm opacity-75 mt-1">Active discussions</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl p-6 shadow-lg transform hover:scale-105 transition-transform">
            <FaThumbtack className="text-3xl mb-3 opacity-80" />
            <div className="text-sm mb-1 opacity-90">Pinned</div>
            <div className="text-4xl font-bold">{stats.pinned}</div>
            <p className="text-sm opacity-75 mt-1">Featured on homepage</p>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
          <div className="flex">
            <FaExclamationTriangle className="text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 font-semibold">
                Instant Publishing Enabled
              </p>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                All client feedback appears on the homepage immediately. Review
                regularly and delete any inappropriate, offensive, or fake content.
                Use <FaThumbtack className="inline mx-1" /> Pin to feature best feedbacks at the top.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <FaSearch className="inline mr-2" />
                Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, message..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <FaFilter className="inline mr-2" />
                Filter by Rating
              </label>
              <select
                value={filterRating}
                onChange={e => setFilterRating(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="rating-desc">Highest Rating</option>
                <option value="rating-asc">Lowest Rating</option>
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredFeedbacks.length} of {feedbacks.length} feedbacks
            {stats.pinned > 0 && (
              <span className="ml-2 text-orange-600 dark:text-orange-400">
                • {stats.pinned} pinned
              </span>
            )}
          </div>
        </div>

        {/* Feedback list */}
        <div className="space-y-4">
          {filteredFeedbacks.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl">
              <FaComments className="text-6xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                No feedback found
              </p>
            </div>
          ) : (
            filteredFeedbacks.map(feedback => {
              const feedbackId = getFeedbackId(feedback);
              
              if (!feedbackId) {
                console.error('Skipping feedback without ID:', feedback);
                return null;
              }
              
              return (
                <div
                  key={feedbackId}
                  className={`bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all ${
                    feedback.is_pinned
                      ? 'ring-2 ring-yellow-400 dark:ring-yellow-500'
                      : ''
                  }`}
                >
                  <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {feedback.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {feedback.name}
                            {feedback.is_pinned && (
                              <span className="text-yellow-500" title="Pinned">
                                <FaThumbtack />
                              </span>
                            )}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <FaEnvelope className="text-xs" />
                              {feedback.email}
                            </span>
                            {feedback.phone && (
                              <span className="flex items-center gap-1">
                                • <FaPhone className="text-xs" />
                                {feedback.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {feedback.city && feedback.country && (
                        <p className="text-sm text-gray-500 dark:text-gray-500 flex items-center gap-1 mb-1">
                          <FaMapMarkerAlt className="text-red-500" />
                          {feedback.city}, {feedback.state}, {feedback.country}
                        </p>
                      )}

                      <p className="text-sm text-gray-500 dark:text-gray-500 flex items-center gap-1">
                        <FaClock className="text-blue-500" />
                        {new Date(feedback.createdAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        ID: {feedbackId.substring(0, 8)}...
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <FaStar
                            key={i}
                            className={
                              i < feedback.rating
                                ? 'text-yellow-400'
                                : 'text-gray-300 dark:text-gray-600'
                            }
                            size={20}
                          />
                        ))}
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 flex items-center gap-1">
                        <FaCheckCircle />
                        Live on Homepage
                      </span>
                      {feedback.is_pinned && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300 flex items-center gap-1">
                          <FaThumbtack />
                          Featured
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {feedback.message}
                    </p>
                  </div>

                  {feedback.image && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FaImage className="text-blue-600" />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Attachment
                        </span>
                      </div>
                      <img
                        src={feedback.image}
                        alt="Feedback attachment"
                        className="max-w-xs rounded-lg shadow-md hover:shadow-xl transition-shadow"
                      />
                    </div>
                  )}

                  {feedback.replies && feedback.replies.length > 0 && (
                    <div className="mb-4 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                      <FaComments />
                      <span className="font-semibold">
                        {feedback.replies.length}{' '}
                        {feedback.replies.length === 1 ? 'reply' : 'replies'}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 pt-4 border-t dark:border-gray-700">
                    <button
                      onClick={() => setSelectedFeedback(feedback)}
                      className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105"
                    >
                      <FaEye /> View Details
                    </button>
                    
                    {/* FIXED: Pin Button */}
                    <button
                      onClick={() => handleTogglePin(feedback, feedback.is_pinned)}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-all transform hover:scale-105 font-semibold ${
                        feedback.is_pinned
                          ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700 shadow-lg'
                          : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white hover:from-gray-500 hover:to-gray-600'
                      }`}
                      title={feedback.is_pinned ? 'Unpin from homepage' : 'Pin to homepage'}
                    >
                      <FaThumbtack />
                      {feedback.is_pinned ? 'Unpin' : 'Pin'}
                    </button>

                    <button
                      onClick={() => handleDelete(feedback)}
                      className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all transform hover:scale-105"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail Modal */}
        {selectedFeedback && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={() => setSelectedFeedback(null)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      Feedback Details
                      {selectedFeedback.is_pinned && (
                        <span className="text-yellow-300">
                          <FaThumbtack />
                        </span>
                      )}
                    </h2>
                    <p className="text-sm opacity-90 mt-1">
                      Complete feedback information
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFeedback(null)}
                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                      Full Name
                    </label>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedFeedback.name}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                    <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                      Email Address
                    </label>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedFeedback.email}
                    </p>
                  </div>
                  {selectedFeedback.phone && (
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                      <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                        Phone Number
                      </label>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {selectedFeedback.phone}
                      </p>
                    </div>
                  )}
                  {selectedFeedback.city && (
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
                      <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                        Location
                      </label>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {selectedFeedback.city}, {selectedFeedback.state},{' '}
                        {selectedFeedback.country}
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
                    <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-2">
                      Rating
                    </label>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className={
                            i < selectedFeedback.rating
                              ? 'text-yellow-400'
                              : 'text-gray-300'
                          }
                          size={24}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                    <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 block mb-2">
                      Submitted On
                    </label>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {new Date(selectedFeedback.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* FIXED: Pin Status in Modal */}
                <div className={`p-4 rounded-xl flex items-center justify-between ${
                  selectedFeedback.is_pinned
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700'
                    : 'bg-gray-50 dark:bg-gray-700'
                }`}>
                  <div className="flex items-center gap-3">
                    <FaThumbtack className={selectedFeedback.is_pinned ? 'text-yellow-500 text-xl' : 'text-gray-400 text-xl'} />
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {selectedFeedback.is_pinned ? 'Pinned to Homepage' : 'Not Pinned'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedFeedback.is_pinned
                          ? 'This feedback is featured in the pinned carousel'
                          : 'Pin this to feature it on the homepage carousel'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleTogglePin(selectedFeedback, selectedFeedback.is_pinned)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      selectedFeedback.is_pinned
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400 dark:bg-gray-600 dark:text-white'
                    }`}
                  >
                    {selectedFeedback.is_pinned ? 'Unpin' : 'Pin'}
                  </button>
                </div>

                <div>
                  <label className="text-lg font-bold text-gray-900 dark:text-white block mb-3">
                    Feedback Message
                  </label>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border-l-4 border-blue-600">
                    <p className="text-gray-900 dark:text-white leading-relaxed whitespace-pre-wrap">
                      {selectedFeedback.message}
                    </p>
                  </div>
                </div>

                {selectedFeedback.image && (
                  <div>
                    <label className="text-lg font-bold text-gray-900 dark:text-white block mb-3">
                      Attachment
                    </label>
                    <img
                      src={selectedFeedback.image}
                      alt="Feedback attachment"
                      className="w-full rounded-xl shadow-lg"
                    />
                  </div>
                )}

                {selectedFeedback.replies &&
                  selectedFeedback.replies.length > 0 && (
                    <div>
                      <label className="text-lg font-bold text-gray-900 dark:text-white block mb-3">
                        Replies ({selectedFeedback.replies.length})
                      </label>
                      <div className="space-y-3">
                        {selectedFeedback.replies.map((reply, index) => {
                          const replyId = reply._id || reply.id || `reply-${index}`;
                          return (
                            <div
                              key={replyId}
                              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="font-bold text-gray-900 dark:text-white">
                                    {reply.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {reply.email}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {new Date(
                                      reply.createdAt
                                    ).toLocaleString()}
                                  </p>
                                </div>
                                <button
                                  onClick={() =>
                                    handleDeleteReply(
                                      selectedFeedback,
                                      replyId
                                    )
                                  }
                                  className="text-red-600 hover:text-red-700 p-2 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                              <p className="text-gray-700 dark:text-gray-300">
                                {reply.message}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>

              <div className="p-6 border-t dark:border-gray-700 flex flex-wrap gap-3">
                {/* FIXED: Pin Button in Modal Footer */}
                <button
                  onClick={() => handleTogglePin(selectedFeedback, selectedFeedback.is_pinned)}
                  className={`flex items-center justify-center gap-2 px-6 py-4 rounded-xl transition-all font-semibold transform hover:scale-105 ${
                    selectedFeedback.is_pinned
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700'
                      : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white hover:from-gray-500 hover:to-gray-600'
                  }`}
                >
                  <FaThumbtack />
                  {selectedFeedback.is_pinned ? 'Unpin' : 'Pin to Homepage'}
                </button>

                <button
                  onClick={() => handleDelete(selectedFeedback)}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-xl hover:from-red-700 hover:to-red-800 transition-all font-semibold flex items-center justify-center gap-2 transform hover:scale-105"
                >
                  <FaTrash /> Delete This Feedback
                </button>
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="px-6 py-4 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageFeedback;